import { EmptyState, Icon, PageHeader, UiLink } from '@nexus/ui';
import { useTranslation } from 'react-i18next';
import { useModules } from '../modules/context';
import { useSession } from '../session/session';
import { visibleSections, type AdminArea } from './sections';

/** Overview of the administration: every section the person may open, with what it is for. */
export function AdminHome() {
  const { t } = useTranslation();
  const session = useSession();
  const sections = visibleSections(session, useModules());

  if (!sections.length) {
    return (
      <EmptyState
        icon="lock"
        title={t('errors.forbiddenTitle')}
        description={t('errors.forbiddenDescription')}
      />
    );
  }

  return (
    <>
      <PageHeader
        title={t('admin.title')}
        description={t('admin.description', { company: session.company?.name ?? '' })}
      />
      <div className="flex flex-col gap-8">
        {(['company', 'platform'] as AdminArea[]).map((area) => {
          const items = sections.filter((section) => section.area === area);
          if (!items.length) return null;
          return (
            <section key={area} aria-labelledby={`admin-area-${area}`}>
              <div className="mb-3">
                <h2 id={`admin-area-${area}`} className="text-base font-semibold text-fg">
                  {t(`admin.areas.${area}`)}
                </h2>
                {area === 'platform' && <p className="text-fg-muted">{t('admin.platformHint')}</p>}
              </div>
              <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {items.map((section) => (
                  <li key={section.id}>
                    <UiLink
                      href={section.path}
                      className="group flex h-full items-start gap-3 rounded-lg border border-line bg-surface p-4 transition-colors transition-fast hover:border-line-strong hover:bg-hover"
                    >
                      <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-brand-subtle text-brand-text">
                        <Icon name={section.icon} className="size-[1.125rem]" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block font-medium text-fg">
                          {t(`admin.sections.${section.id}.label`)}
                        </span>
                        <span className="block text-fg-muted">
                          {t(`admin.sections.${section.id}.description`)}
                        </span>
                      </span>
                      <Icon
                        name="chevron-right"
                        className="mt-0.5 text-fg-subtle transition-transform transition-fast group-hover:translate-x-0.5"
                      />
                    </UiLink>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>
    </>
  );
}
